import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { trackAttendeeClickGenerate } from '../../analytics';

import { FancyBtn, ClipReselectInfo } from '../../components/custom';
import { PageWrapper, ContentHeader } from '../../components/layout/content';
import { ErrorModal } from '../../components/modals';

import { getOneCreation } from '../../services/api/creations';
import { getEventPublic } from '../../services/api/events';
import { getTemplateData } from '../../services/api/template';
import { getAttendeeReviewData } from '../../services/videoBuilderService';

import { useVideoBuilderStore } from '../../stores/videoBuilder';

import {
  PublicEvent,
  Person,
  HlReel,
  Shot,
  FocusedClip,
  WildcardFiller,
  PageError,
  ModalError,
} from '../../types';
import { goTo, isEmpty, msecToSec, getShareLink } from '../../utils';

import HlReelPreview from '../VideoBuilder/Analysis/components/hlReelPreview';
import PersonClipsHeading from '../VideoBuilder/Analysis/components/personClipsHeading';
import { getClipSelections } from '../VideoBuilder/components/getAnalysisData';
import ThumbnailsContainer from '../VideoBuilder/components/thumbnailsContainer';
import GenerateModal from './components/generateModal';

const ReviewVideo = () => {
  const { faceId = '' } = useParams();

  const { clipSelections, setClipSelections } = useVideoBuilderStore();

  const [event, setEvent] = useState<PublicEvent | null>(null);

  const [person, setPerson] = useState<Person | null>(null);
  const [hlReel, setHlReel] = useState<HlReel | null>(null);
  const [requiredShots, setRequiredShots] = useState<Shot[]>([]);

  const [focusedClip, setFocusedClip] = useState<FocusedClip | null>(null);

  const [isCollapse, setIsCollapse] = useState<boolean | undefined>(undefined);
  const [isShowLowScore, setIsShowLowScore] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState<ModalError>(null);

  const { clips, lowScoreClips } = useMemo(() => {
    const lowScoreClips = person?.lowScoreClips ?? [];
    const clips = (person?.clips ?? []).concat(
      isShowLowScore ? lowScoreClips : [],
    );

    return { clips, lowScoreClips };
  }, [person, isShowLowScore]);

  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchData();

      setIsLoading(false);
    };

    onMount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceId]);

  const fetchData = async () => {
    try {
      const { person, eventId } = await getAttendeeReviewData(faceId);
      const event = await getEventPublic(eventId);

      // if no person or event data – show error
      if (!event || !person) {
        setPageError(404);
        return;
      }

      // if creation is already generated – go to share page
      const creation = await getOneCreation(faceId);
      if (!!creation) {
        goTo(getShareLink(eventId, faceId));
        return;
      }

      const { hasTemplate, hlReel, requiredShots } = await getTemplateData(
        eventId,
        true,
      );

      if (!hasTemplate) {
        setError({
          heading: 'Analysis in progress',
          subheading: 'Looks like the data is still cooking, check back later!',
        });
        return;
      }

      const clipSelectionsTemp = getClipSelections({
        people: [person],
        requiredShots,
      });

      setEvent(event);
      setPerson(person);
      setHlReel(hlReel);
      setRequiredShots(requiredShots);
      setClipSelections(clipSelectionsTemp);
    } catch (err) {
      console.error(err);
      setError({ subheading: 'Error fetching some data' });
    }
  };

  // clear the focused clip when clicked outside of thumbnails container or highlight reel preview
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isEmpty(focusedClip)) {
        const thumbnailContainerEl = document.getElementById(faceId);
        const hlReelPreviewEl = document.getElementById(
          'hl-reel-preview-person',
        );

        if (
          !thumbnailContainerEl?.contains(e.target as Node) &&
          !hlReelPreviewEl?.contains(e.target as Node)
        )
          setFocusedClip(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [focusedClip, faceId]);

  // toggle whether low-score clips are shows
  const toggleShowLowScoreClips = () => {
    const newIsShow = !isShowLowScore;

    // open full clips list, then reset collapse state
    if (newIsShow) {
      setIsCollapse(false);
      setTimeout(() => setIsCollapse(undefined), 15);
    }

    setIsShowLowScore(newIsShow);
  };

  // change clip selection for particular wildcard
  const handleAssignFocusedClip = (shotIndex: number) => {
    if (!focusedClip || !clipSelections) return;

    let clipSelectionsTemp = [...clipSelections];

    // find person selections data – here, array has only one person
    const foundSelections = clipSelectionsTemp[0];

    // set selection of focused clip, or remove if it is already selected
    foundSelections?.wildcardsFiller.forEach((w: WildcardFiller) => {
      if (w.clipId === focusedClip.clipId) w.clipId = null;
      else if (w.wildCardShotIndex === shotIndex) w.clipId = focusedClip.clipId;
    });

    setClipSelections(clipSelectionsTemp);
  };

  return (
    <PageWrapper
      type="header-only"
      title="Review Your Video"
      isLoading={isLoading}
      pageError={pageError}
      className="gap-4"
      noTips
    >
      {event && person && !error && (
        <React.Fragment>
          <ContentHeader
            title={
              <div className="flex gap-4 items-center">
                <img
                  className="w-10 aspect-square rounded-full object-cover"
                  src={person.img}
                  alt={`${person.face.name} Icon`}
                />

                <div>{person.face.name} – Review your video</div>
              </div>
            }
            subtitle={`Craft your perfect video from ${event.name} – because your memories deserve the VIP treatment!`}
          />

          <ClipReselectInfo requiredShots={requiredShots} />

          <HlReelPreview
            hlReel={hlReel}
            person={person}
            focusedClip={focusedClip}
            handleAssignFocusedClip={handleAssignFocusedClip}
          />

          <div>
            <PersonClipsHeading
              clips={clips}
              lowScoreClips={lowScoreClips}
              isShowLowScore={isShowLowScore}
              toggleShowLowScoreClips={toggleShowLowScoreClips}
            />

            <ThumbnailsContainer
              groupId={person.face.elementId}
              videos={clips}
              collapseRequest={isCollapse}
              handleClick={(video) =>
                setFocusedClip({
                  clipId: video.elementId,
                  duration: msecToSec(video.segment?.durationMilliseconds),
                })
              }
              focusedClipId={focusedClip?.clipId}
            />
          </div>

          <div className="flex flex-col items-center text-center mt-6 gap-2">
            <div className="text-lg font-bold">
              Ready to generate your personalised highlight reel?
            </div>

            <div className="italic">
              We'll generate reel based on the selections above.
            </div>

            <FancyBtn
              text="Generate"
              onClick={() => {
                setShowGenerateModal(true);
                trackAttendeeClickGenerate();
              }}
              isDisabled={
                !clipSelections[0]?.wildcardsFiller.every((clip) => clip.clipId)
              }
            />
          </div>

          <GenerateModal
            show={showGenerateModal}
            onHide={() => setShowGenerateModal(false)}
            eventId={event.eventId}
            faceId={faceId}
          />
        </React.Fragment>
      )}

      <ErrorModal
        show={!!error}
        heading={error?.heading ?? 'Something went wrong'}
        subheading={error?.subheading}
        isTryAgainBtn
        isContactUs
      />
    </PageWrapper>
  );
};

export default ReviewVideo;
